import { getAuth } from "../services/localStorage";

export function isFilled(value) {
  return String(value ?? "").trim() !== "";
}

export function isPositiveNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0;
}

export function isNonNegativeNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0;
}

export function isEmail(value) {
  const text = String(value || "").trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text);
}

export function pickOptionId(item) {
  if (item == null || item === "") return "";
  if (typeof item !== "object") return item;
  if (item.value != null && item.value !== "") return item.value;
  if (item.id != null && item.id !== "") return item.id;
  return "";
}

/** Match a cascade option by id/value or by display label. */
export function findOptionRow(options, target) {
  const list = Array.isArray(options) ? options : [];
  if (!list.length || target == null || target === "") return null;

  const targetId = String(pickOptionId(target) ?? "").trim();
  const targetLabels = [
    typeof target === "object"
      ? target.display ||
        target.model_name ||
        target.fuel_type ||
        target.year ||
        target.brand_name ||
        target.name ||
        target.label
      : target,
  ]
    .filter((v) => v != null && String(v).trim() !== "")
    .map((v) => String(v).trim().toLowerCase());

  if (targetId) {
    const byId = list.find(
      (item) => String(pickOptionId(item) ?? "").trim() === targetId
    );
    if (byId) return byId;
  }

  for (const label of targetLabels) {
    const byLabel = list.find((item) => {
      const candidates = [
        item?.display,
        item?.model_name,
        item?.fuel_type,
        item?.year,
        item?.brand_name,
        item?.name,
        item?.label,
      ]
        .filter((v) => v != null && String(v).trim() !== "")
        .map((v) => String(v).trim().toLowerCase());
      return candidates.includes(label);
    });
    if (byLabel) return byLabel;
  }

  return null;
}

/** Prefer numeric/option ids; resolve brand name strings against the brands list. */
export function resolveBrandOption(source, brands = []) {
  if (source == null || source === "") return null;
  const list = Array.isArray(brands) ? brands : [];

  if (typeof source === "object") {
    const id = pickOptionId(source);
    if (id !== "" && id != null) {
      const byId = findOptionRow(list, source) || findOptionRow(list, id);
      if (byId) return byId;
      return source;
    }
    const name =
      source.brand_name || source.name || source.label || source.display || "";
    if (name) {
      const byName = findOptionRow(list, name);
      if (byName) return byName;
    }
    return source;
  }

  const asText = String(source).trim();
  if (!asText) return null;
  // Pure numeric id
  if (/^\d+$/.test(asText)) {
    return findOptionRow(list, asText) || { id: asText, brand_name: asText };
  }
  // Name string from vehicle list rows
  return findOptionRow(list, asText) || { brand_name: asText };
}

export function authHeaders(extra = {}) {
  const auth = getAuth();
  const headers = { ...extra };
  if (auth?.token) {
    headers.Authorization = `Token ${auth.token}`;
  }
  return headers;
}

export function apiErrorMessage(json, fallback) {
  if (!json || typeof json !== "object") return fallback;
  const message = json.message;
  if (typeof message === "string" && message.trim()) return message;
  if (message && typeof message === "object") {
    const description = message.description || message.detail || message.text;
    if (description) return String(description);
  }
  if (json.detail) return String(json.detail);
  if (json.error) return String(json.error);
  const fieldErrors = Object.values(json).find(
    (value) => Array.isArray(value) && value.length
  );
  if (fieldErrors) return String(fieldErrors[0]);
  return fallback;
}

export async function readApiResponse(response, fallback = "Request failed.") {
  let json = null;
  try {
    json = await response.json();
  } catch (e) {
    json = null;
  }
  const ok =
    Boolean(response?.ok) &&
    json?.message?.type !== "error" &&
    json?.message?.type !== "fail";
  return {
    ok,
    json,
    description: ok
      ? json?.message?.description || ""
      : apiErrorMessage(json, fallback),
  };
}

export function unwrapList(json) {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.data)) return json.data;
  return [];
}
